import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useSelector } from 'react-redux';

const LiveShowsSetup = () => {
  const navigate = useNavigate();
  const bandsintownSettings = useSelector(state => state.bandsintown?.settings);
  const [bandsintownConnected, setBandsintownConnected] = useState(false);

  useEffect(() => {
    // Check if Bandsintown was connected during onboarding
    try {
      const connectorStatuses = JSON.parse(localStorage.getItem('connectorStatuses') || '{}');
      const localBandsintownSettings = localStorage.getItem('bandsintownSettings');
      
      if (connectorStatuses?.bandsintown) {
        setBandsintownConnected(true);
      } else if (localBandsintownSettings) {
        const settings = JSON.parse(localBandsintownSettings);
        setBandsintownConnected(!!(settings.apiKey && settings.artistName));
      } else if (bandsintownSettings?.apiKey && bandsintownSettings?.artistName) {
        setBandsintownConnected(true);
      }
    } catch (error) {
      console.error('Error checking Bandsintown connection:', error);
    }
  }, [bandsintownSettings]);

  const setupOptions = [
    {
      id: 'bandsintown-import',
      title: 'Import from Bandsintown',
      subtitle: '(Fast Fill Shows)',
      description: 'Quickly pull in your past and upcoming shows from Bandsintown.',
      extendedDescription: 'Fast setup with lighter detail. Includes the basics: show date, city, venue, country, and status. Does not include financials, deal terms, or ticket performance.',
      note: 'This is typically already done during initial onboarding.',
      icon: (
        <svg className="w-5 h-5 text-[#00CEC8]" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ),
      iconBg: 'rgba(0, 206, 200, 0.15)',
      completed: bandsintownConnected,
      action: () => navigate('/connectors/bandsintown')
    },
    {
      id: 'booking-data',
      title: 'Add agent, promoter and additional booking data',
      subtitle: '(Complete Show Details)',
      description: 'Add additional information and complete the show record by importing a CSV file from your agent, promoter, or internal booking spreadsheet.',
      extendedDescription: 'Capture richer details that are not available via Bandsintown. Adding these details provides the most complete and accurate view of your bookings.',
      icon: (
        <svg className="w-5 h-5 text-[#FF6B35]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      iconBg: 'rgba(255, 107, 53, 0.15)',
      action: () => navigate('/bookings/bulk-import')
    },
    {
      id: 'auto-flow',
      title: 'Set up Auto Flow of booking data',
      subtitle: '(Real Time Sync)',
      description: 'As new shows are booked or updated, use Dizee Sheets to keep your touring data automatically in sync.',
      extendedDescription: 'Simply convert your existing booking spreadsheet into a Dizee Sheet and continue using Google Sheets exactly how you do today. Any changes made will flow into Dizee in real time.',
      icon: (
        <svg className="w-5 h-5 text-[#00CED1]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      ),
      iconBg: 'rgba(0, 206, 209, 0.15)',
      action: () => navigate('/connectors/google-sheets')
    },
    {
      id: 'manual-add',
      title: 'Manually add new data via DIZEE AI or Bookings',
      subtitle: '(Alternative option)',
      description: "If you don't have existing booking spreadsheets or don't plan to use Dizee Sheets, you can also add shows manually.",
      extendedDescription: "Simply open the AI Chat to describe the show in natural language or go into the Bookings app and click 'Manual' to add or update shows one at a time.",
      icon: (
        <svg className="w-5 h-5 text-[#A78BFA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
        </svg>
      ),
      iconBg: 'rgba(167, 139, 250, 0.15)',
      action: () => navigate('/bookings/manual-add')
    }
  ];

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Live Shows Setup</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate('/data')}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Page Title */}
            <div className="mb-2">
              <h1 className="text-white text-[20px] font-semibold">Live Shows Setup</h1>
              <p className="text-[rgba(255,255,255,0.5)] text-[13px]">(Bookings)</p>
            </div>

            {/* Description */}
            <div className="mb-8">
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.6]">
                Import your Live Show history and upcoming dates into Dizee to create a complete, real-time view of your touring activity that stays organized and always up to date.
              </p>
            </div>

            {/* Setup Options */}
            <div className="space-y-4">
              {setupOptions.map((option, index) => (
                <div
                  key={option.id}
                  onClick={option.action}
                  className={`w-full bg-[rgba(255,255,255,0.03)] border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                    option.completed 
                      ? 'border-green-500 border-opacity-40 hover:border-opacity-60' 
                      : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border border-[rgba(255,255,255,0.1)]"
                      style={{ backgroundColor: option.iconBg }}
                    >
                      {option.completed ? (
                        <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        option.icon
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white text-[13px] font-medium">
                          {option.title}
                        </h3>
                        {option.completed && (
                          <span className="text-green-500 text-[10px] bg-green-500 bg-opacity-15 px-2 py-0.5 rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      {option.subtitle && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-2">
                          {option.subtitle}
                        </p>
                      )}
                      <p className="text-[rgba(255,255,255,0.55)] text-[11px] leading-[1.5] mb-2">
                        {option.description}
                      </p>
                      {option.extendedDescription && (
                        <p className="text-[rgba(255,255,255,0.45)] text-[11px] leading-[1.5]">
                          {option.extendedDescription}
                        </p>
                      )}
                      {option.note && (
                        <p className="text-[rgba(255,255,255,0.4)] text-[10px] mt-2 italic">
                          {option.note}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default LiveShowsSetup;

